import type {
  WeeklyReportPrintModel,
  PrintProgressStatus,
} from "./weeklyReportPrintModel";

/**
 * R2 — the DEDICATED Weekly Team Report print document.
 *
 * A pure, presentational component rendered from the allow-listed
 * `WeeklyReportPrintModel` only. It imports NO trpc client, NO mutations, and
 * receives NO lifecycle/authority/workflow state, so it is STRUCTURALLY
 * incapable of rendering stale/shared-input banners, updated-draft preparation
 * state, authority actions, reportFlow, lifecycle buttons, internal generation
 * data, or private data — regardless of CSS. On screen it is display:none; in
 * print it is the only thing shown (the app page is print-hidden by TeamReport).
 *
 * It renders ONE selected revision (approved OR draft) — never both. A DRAFT
 * watermark + header note mark authority draft prints; approved prints carry
 * neither. The mandatory trust footer is always present.
 */

const t = (isAr: boolean, en: string, ar: string) => (isAr ? ar : en);

const STATUS_LABEL: Record<PrintProgressStatus, { en: string; ar: string }> = {
  on_track: { en: "On track", ar: "على المسار" },
  needs_attention: { en: "Needs attention", ar: "يحتاج انتباهًا" },
  blocked: { en: "Blocked", ar: "معطَّل" },
  completed_this_week: { en: "Completed this week", ar: "أُنجز هذا الأسبوع" },
};
const statusLabel = (s: PrintProgressStatus, isAr: boolean) =>
  isAr ? STATUS_LABEL[s].ar : STATUS_LABEL[s].en;

const impLabel = (imp: number, isAr: boolean) =>
  (isAr ? ["", "عالٍ", "متوسط", "منخفض"] : ["", "High", "Medium", "Low"])[imp] ?? "";

// Scoped print stylesheet. `@page` is document-level (the print doc is the only
// thing printed); everything else is prefixed `.wrpd` so it never leaks onto
// the screen UI. Pagination rules keep priority blocks + the trust footer
// whole and headings with their content.
const PRINT_CSS = `
@media print { @page { size: A4 portrait; margin: 16mm; } }
.wrpd { color:#111; background:#fff; font-size:12pt; line-height:1.5; font-family: inherit; }
.wrpd * { box-shadow:none !important; }
.wrpd .wrpd-h1 { font-size:18pt; font-weight:600; margin:0; }
.wrpd .wrpd-meta { font-size:10pt; color:#333; margin:2px 0 0; }
.wrpd .wrpd-draftline { font-size:10pt; color:#333; margin:2px 0 0; }
.wrpd .wrpd-rule { border:0; border-top:1px solid #bbb; margin:10px 0 0; }
.wrpd .wrpd-section { margin-top:16px; break-inside:avoid; page-break-inside:avoid; }
.wrpd .wrpd-h2 { font-size:11pt; font-weight:600; letter-spacing:.06em; text-transform:uppercase; color:#333; margin:0 0 6px; padding-bottom:3px; border-bottom:1px solid #ccc; break-after:avoid; page-break-after:avoid; }
.wrpd .wrpd-body { font-size:12pt; white-space:pre-wrap; }
.wrpd ul { margin:0; padding-inline-start:18px; }
.wrpd .wrpd-priority { break-inside:avoid; page-break-inside:avoid; margin-top:12px; }
.wrpd .wrpd-priority-heading { break-after:avoid; page-break-after:avoid; break-inside:avoid; page-break-inside:avoid; margin-bottom:3px; }
.wrpd .wrpd-priority-title { display:block; font-weight:600; font-size:12.5pt; }
.wrpd .wrpd-status-line { margin-top:2px; }
.wrpd .wrpd-status { display:inline-block; font-size:9.5pt; border:1px solid #999; border-radius:3px; padding:0 6px; margin-inline-start:6px; }
.wrpd .wrpd-status-line .wrpd-status { margin-inline-start:0; }
.wrpd .wrpd-line { font-size:11pt; margin-top:3px; }
.wrpd .wrpd-line-label { font-weight:600; }
.wrpd .wrpd-receipts-heading { display:flex; align-items:center; gap:6px; }
.wrpd .wrpd-trust { break-inside:avoid; page-break-inside:avoid; margin-top:22px; border-top:1px solid #bbb; padding-top:8px; font-size:9.5pt; color:#444; }
.wrpd .wrpd-footer { break-inside:avoid; page-break-inside:avoid; margin-top:10px; font-size:8.5pt; color:#666; }
.wrpd .wrpd-wm { position:fixed; inset:0; display:flex; align-items:center; justify-content:center; pointer-events:none; z-index:0; }
.wrpd .wrpd-wm span { font-size:80pt; font-weight:700; color:#555; opacity:.12; transform:rotate(-30deg); letter-spacing:.12em; }
.wrpd .wrpd-content { position:relative; z-index:1; }
`;

function Priority({ p, isAr, i }: { p: WeeklyReportPrintModel["priorities"][number]; isAr: boolean; i: number }) {
  return (
    <div className="wrpd-priority" data-testid={`report-print-priority-${i}`}>
      {/* Priority TITLE first, then the human-declared status directly below —
          kept together across page breaks. The title comes verbatim from the
          selected report content (never inferred by position). */}
      <div className="wrpd-priority-heading">
        <span className="wrpd-priority-title" data-testid={`report-print-priority-title-${i}`}>
          {p.title}
        </span>
        <div className="wrpd-status-line">
          <span className="wrpd-status" data-testid={`report-print-priority-status-${i}`}>
            {statusLabel(p.status, isAr)}
          </span>
        </div>
      </div>
      {p.note ? <p className="wrpd-line wrpd-body">{p.note}</p> : null}
      {p.blocker ? (
        <p className="wrpd-line">
          <span className="wrpd-line-label">{t(isAr, "Blocker", "العائق")}: </span>
          {p.blocker}
        </p>
      ) : null}
      {p.nextStep ? (
        <p className="wrpd-line">
          <span className="wrpd-line-label">{t(isAr, "Next step", "الخطوة التالية")}: </span>
          {p.nextStep}
        </p>
      ) : null}
      {p.decisionRequired ? (
        <p className="wrpd-line">
          <span className="wrpd-line-label">{t(isAr, "Decision required", "قرار مطلوب")}: </span>
          {p.decisionRequired}
        </p>
      ) : null}
    </div>
  );
}

export function WeeklyReportPrintDocument({ model }: { model: WeeklyReportPrintModel }) {
  const isAr = model.isAr;
  const isDraft = model.status === "draft";
  return (
    <div
      className="wrpd hidden print:block"
      data-testid="report-print-document"
      dir={isAr ? "rtl" : "ltr"}
      aria-hidden
    >
      <style>{PRINT_CSS}</style>

      {/* DRAFT watermark — authority draft prints only; repeats per page. The
          draft state is ALSO stated textually in the header (not colour-alone). */}
      {isDraft ? (
        <div className="wrpd-wm" data-testid="report-print-watermark">
          <span>{t(isAr, "DRAFT", "مسودة")}</span>
        </div>
      ) : null}

      <div className="wrpd-content">
        {/* HEADER */}
        <header data-testid="report-print-header">
          <h1 className="wrpd-h1">{t(isAr, "Weekly Team Report", "تقرير الفريق الأسبوعي")}</h1>
          <p className="wrpd-meta">{model.teamName}</p>
          <p className="wrpd-meta">{model.weekLabel}</p>
          {isDraft ? (
            <>
              <p className="wrpd-meta" style={{ fontWeight: 600 }}>{t(isAr, "DRAFT", "مسودة")}</p>
              <p className="wrpd-draftline">
                {t(isAr, "Only visible to leads until approved", "ظاهرة للقادة فقط حتى الاعتماد")}
              </p>
            </>
          ) : model.approvedDate ? (
            <p className="wrpd-meta">
              {t(isAr, "Approved", "اعتُمد")} {model.approvedDate}
            </p>
          ) : null}
          <hr className="wrpd-rule" />
        </header>

        {/* 1. EXECUTIVE SUMMARY */}
        {model.intro ? (
          <section className="wrpd-section" data-testid="report-print-exec">
            <h2 className="wrpd-h2">{t(isAr, "Executive summary", "الملخّص التنفيذي")}</h2>
            <p className="wrpd-body">{model.intro}</p>
          </section>
        ) : null}

        {/* 2. TEAM FOCUS THIS WEEK */}
        <section className="wrpd-section" data-testid="report-print-focus">
          <h2 className="wrpd-h2">{t(isAr, "Team focus this week", "تركيز الفريق هذا الأسبوع")}</h2>
          {model.focus.length === 0 ? (
            <p className="wrpd-body">{t(isAr, "No weekly focus set for this week.", "لا يوجد تركيز محدَّد لهذا الأسبوع.")}</p>
          ) : (
            <ul>
              {model.focus.map((f, i) => (
                <li key={i} data-testid={`report-print-focus-${i}`}>
                  {f.title}
                  {f.note ? <span className="wrpd-body"> — {f.note}</span> : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 3–5. WEEKLY PROGRESS (declared per focused priority; blocker / next
            step / decision are kept WITHIN each priority block, not duplicated). */}
        <section className="wrpd-section" data-testid="report-print-progress">
          <h2 className="wrpd-h2">{t(isAr, "Weekly progress", "التقدم الأسبوعي")}</h2>
          {model.priorities.length > 0 ? (
            model.priorities.map((p, i) => <Priority key={i} p={p} isAr={isAr} i={i} />)
          ) : model.legacyProgress ? (
            // Frozen legacy reports have no per-priority declarations — a single
            // team-level weekly update. Give it a CLEAR heading so it is never a
            // title-less, status-only block (it is team-level, not a priority).
            <div className="wrpd-priority" data-testid="report-print-legacy-progress">
              <div className="wrpd-priority-heading">
                <span className="wrpd-priority-title">{t(isAr, "Team update", "تحديث الفريق")}</span>
                <div className="wrpd-status-line">
                  <span className="wrpd-status">{statusLabel(model.legacyProgress.status, isAr)}</span>
                </div>
              </div>
              <p className="wrpd-line wrpd-body">{model.legacyProgress.summary}</p>
              {model.legacyProgress.blocker ? (
                <p className="wrpd-line">
                  <span className="wrpd-line-label">{t(isAr, "Blocker", "العائق")}: </span>
                  {model.legacyProgress.blocker}
                </p>
              ) : null}
              {model.legacyProgress.nextStep ? (
                <p className="wrpd-line">
                  <span className="wrpd-line-label">{t(isAr, "Next step", "الخطوة التالية")}: </span>
                  {model.legacyProgress.nextStep}
                </p>
              ) : null}
            </div>
          ) : (
            <p className="wrpd-body">{t(isAr, "No weekly progress declared for this week.", "لم يُعلَن تقدّم أسبوعي لهذا الأسبوع.")}</p>
          )}
        </section>

        {/* 6. APPROVED RECEIPT HIGHLIGHTS — supporting evidence, never a status source. */}
        <section className="wrpd-section" data-testid="report-print-receipts">
          <h2 className="wrpd-h2 wrpd-receipts-heading">
            <span aria-hidden>▤</span>
            {t(isAr, "Approved receipt highlights", "أبرز الملخّصات المعتمدة")}
          </h2>
          {model.highlights.length === 0 ? (
            <p className="wrpd-body">{t(isAr, "No shared receipt highlights yet.", "لا توجد ملخّصات مُشارَكة بارزة بعد.")}</p>
          ) : (
            <ul>
              {model.highlights.map((h, i) => (
                <li key={i} data-testid={`report-print-receipt-${i}`}>
                  <span style={{ fontWeight: 600 }}>{h.headline}</span>
                  {h.lines.length > 0 ? (
                    <ul>
                      {h.lines.map((l, j) => (
                        <li key={j} className="wrpd-body">{l}</li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* 7. ACTIVE TEAM DIRECTION — the STORED selected report content only
            (never a live Team Direction query, never title-string filtering).
            Labelled "Active team direction" so it reads as the full active
            direction snapshot captured at generation, not a curated overview.
            Approved history is preserved verbatim; test/filler directions that
            were active at generation stay in an already-approved revision (the
            renderer must not rewrite history — archive them upstream instead). */}
        <section className="wrpd-section" data-testid="report-print-directions">
          <h2 className="wrpd-h2">{t(isAr, "Active team direction", "التوجيه النشط للفريق")}</h2>
          {model.directions.length === 0 ? (
            <p className="wrpd-body">{t(isAr, "No active team direction.", "لا يوجد توجيه نشط للفريق.")}</p>
          ) : (
            <ul>
              {model.directions.map((d, i) => (
                <li key={i} data-testid={`report-print-direction-${i}`}>
                  {d.title}
                  {impLabel(d.importance, isAr) ? (
                    <span className="wrpd-status">{impLabel(d.importance, isAr)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* MANDATORY trust footer — verbatim, always present (never omitted to
            save space, never dropped by empty optional sections). */}
        <div className="wrpd-trust" data-testid="report-print-trust">
          {t(
            isAr,
            "Built from shared team inputs and approved receipts. Nothing here was computed from private work.",
            "مبني على مدخلات الفريق المُشارَكة والملخّصات المعتمدة. لا شيء هنا محسوب من عمل خاص.",
          )}
        </div>

        {/* Document footer — team + week + trust identity (no ids/timestamps). */}
        <div className="wrpd-footer" data-testid="report-print-footer">
          {model.teamName} · {model.weekLabel}
        </div>
      </div>
    </div>
  );
}
